import type { ApiResponse } from '@shared/types'
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { orderBy } from 'lodash'
import { queryClient } from 'v2/configs/rq-setup'
import { useIntegrationRollout } from 'v2/shared/hooks/use-integration-rollout'
import { type CustomField, externalService } from 'v2/shared/services/external-apps-service'
import type { IntegrationSourceGateway } from 'v2/shared/services/integration-source-gateway'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { externalAppsQueryKeys, flowQueryKeys } from 'v2/shared/utils/query-key-management'
import type {
  IntegrationSource,
  OrgAppConnectionPayload,
  RAciBridgeConnectResponse,
  RExternalOrgApp,
  ROrgAppConnectionV2,
} from 'v2/types/api/settings'
import { AppCategory } from 'v2/types/api/settings'
import { errorToast, successToast } from 'v2/utils/toasts'
import type { IntegrationConnectionLoadResult } from './utils/connection-rollout'

export const invalidateExternalAppQueries = (client: QueryClient = queryClient) => {
  client.invalidateQueries({ queryKey: externalAppsQueryKeys().externalOrgAppsBase })
  client.invalidateQueries({ queryKey: flowQueryKeys().appVariablesBase })
  client.invalidateQueries({ queryKey: flowQueryKeys().mcpToolsBase })
}

const assertRolloutResolved = (isResolved: boolean) => {
  if (!isResolved) throw new Error('Integration rollout is still loading')
}

export const useReconnectOrgAppConnectionMutation = (
  gatewayOverride?: IntegrationSourceGateway,
) => {
  const rollout = useIntegrationRollout()
  const gateway = gatewayOverride ?? rollout.gateway
  return useMutation({
    mutationFn: async ({ appId, source }: { appId: string; source?: IntegrationSource }) => {
      assertRolloutResolved(rollout.isResolved)
      const response = await gateway.reconnect(source, appId, window.location.href)
      if (source === 'aci') {
        const data = response.data as RAciBridgeConnectResponse
        return {
          oauth_redirect_url: data.oauth_redirect_url ?? data.consent_url,
          connect_session_token: undefined,
          org_app: undefined,
        }
      }
      const data = response.data as ROrgAppConnectionV2
      return {
        oauth_redirect_url: data.oauth_redirect_url,
        connect_session_token: data.connect_session_token,
        org_app: data.org_app,
      }
    },
    onError: (err) => {
      const errorText = getErrorText(err as AxiosError<ApiResponse<{ error: string }>>)
      errorToast({
        title: 'Error',
        description: errorText?.toString() || 'Failed to reconnect app',
      })
    },
  })
}

export const useUpdateOrgAppConnectionMutation = (gatewayOverride?: IntegrationSourceGateway) => {
  const rollout = useIntegrationRollout()
  const gateway = gatewayOverride ?? rollout.gateway
  return useMutation({
    mutationFn: async ({
      payload,
      appId,
      source,
    }: {
      payload: OrgAppConnectionPayload
      appId: string
      source?: IntegrationSource
    }) => {
      assertRolloutResolved(rollout.isResolved)
      return (await gateway.updateConnection(source, appId, payload)).data
    },
    onSuccess: () => invalidateExternalAppQueries(),
    onError: (err) => {
      const errorText = getErrorText(err as AxiosError<ApiResponse<{ error: string }>>)
      errorToast({
        title: 'Error',
        description: errorText?.toString() || 'Failed to update connection',
      })
    },
  })
}

export const useDeleteOrgAppConnectionMutation = (gatewayOverride?: IntegrationSourceGateway) => {
  const rollout = useIntegrationRollout()
  const gateway = gatewayOverride ?? rollout.gateway
  return useMutation({
    mutationFn: async ({ appId, source }: { appId: string; source?: IntegrationSource }) => {
      assertRolloutResolved(rollout.isResolved)
      return (await gateway.deleteConnection(source, appId)).data
    },
    onSettled: () => invalidateExternalAppQueries(),
    onError: (err) => {
      const errorText = getErrorText(err as AxiosError<ApiResponse<{ error: string }>>)
      errorToast({
        title: 'Error',
        description: errorText?.toString() || 'Failed to delete connection',
      })
    },
  })
}

const emptyConnectionLoadResult = (): IntegrationConnectionLoadResult => ({
  connections: [],
})

export const addPendingShopifyConnection = (
  current: IntegrationConnectionLoadResult | undefined,
  aciEnabled = false,
): IntegrationConnectionLoadResult => {
  const result = current ?? emptyConnectionLoadResult()
  if (aciEnabled) return result
  if (result.connections.some((app) => app.name === 'Shopify')) return result

  const pendingShopify = {
    id: 'pending-shopify-install',
    name: 'Shopify',
    provider: 'shopify',
    state: 'IN-PROGRESS',
    priority: 0,
    integrated_by: { id: '', name: '', email: '' },
    created_on: '',
    logo_url: '/img/Shopify.png',
    connection_details: null,
    app_category: AppCategory.ECOMMERCE,
    is_custom_fields_enabled: false,
    custom_field_entity: [],
    account_identifier: {},
    users_with_access: [],
    actions: [],
    integration_source: 'nango',
  } satisfies RExternalOrgApp

  return {
    ...result,
    connections: orderBy([...result.connections, pendingShopify], ['name'], ['asc']),
  }
}

export const finishShopifyInstallRequest = async (
  shopifyInstallId: string,
  aciEnabled = false,
  finishInstall = externalService.finishShopifyInstall,
) => {
  if (aciEnabled) {
    throw new Error(
      'Legacy Shopify installation is unavailable while the new integrations service is enabled',
    )
  }
  return (await finishInstall({ shopify_install_id: shopifyInstallId })).data
}

export const useFinishShopifyInstallMutation = () => {
  const client = useQueryClient()
  const rollout = useIntegrationRollout()
  const queryKey = externalAppsQueryKeys().externalOrgAppsV3(undefined, rollout.mode)
  return useMutation({
    mutationFn: (shopifyInstallId: string) => {
      assertRolloutResolved(rollout.isResolved)
      return finishShopifyInstallRequest(shopifyInstallId, rollout.useAciIntegrations)
    },
    onMutate: async () => {
      await client.cancelQueries({ queryKey })
      const previous = client.getQueryData<IntegrationConnectionLoadResult>(queryKey)
      if (!rollout.useAciIntegrations) {
        client.setQueryData<IntegrationConnectionLoadResult>(queryKey, (current) =>
          addPendingShopifyConnection(current, false),
        )
      }
      return { previous }
    },
    onError: (err, _variables, context) => {
      client.setQueryData(queryKey, context?.previous)
      const errorText = getErrorText(err as AxiosError<ApiResponse<{ error: string }>>)
      errorToast({
        title: 'Installation failed',
        description: errorText?.toString() || 'Failed to install Shopify app',
      })
    },
    onSuccess: () => invalidateExternalAppQueries(),
  })
}

export const useUpdateCustomFieldsMutation = (gatewayOverride?: IntegrationSourceGateway) => {
  const client = useQueryClient()
  const rollout = useIntegrationRollout()
  const gateway = gatewayOverride ?? rollout.gateway
  return useMutation({
    mutationFn: async ({
      appId,
      fieldsByEntity,
      source,
    }: {
      appId: string
      fieldsByEntity: Record<string, CustomField[]>
      source?: IntegrationSource
    }) => {
      assertRolloutResolved(rollout.isResolved)
      return (await gateway.updateCustomFields(source, appId, fieldsByEntity)).data
    },
    onSuccess: (_, { appId, source }) => {
      client.invalidateQueries({ queryKey: externalAppsQueryKeys().customFields(appId, source) })
      successToast({ title: 'Success', description: 'Custom fields updated successfully' })
    },
    onError: (err) => {
      const errorText = getErrorText(err as AxiosError<ApiResponse<{ error: string }>>)
      errorToast({
        title: 'Error',
        description: errorText?.toString() || 'Failed to update custom fields',
      })
    },
  })
}

export const useResyncCustomFieldsMutation = (gatewayOverride?: IntegrationSourceGateway) => {
  const client = useQueryClient()
  const rollout = useIntegrationRollout()
  const gateway = gatewayOverride ?? rollout.gateway
  return useMutation({
    mutationFn: async ({
      appId,
      entities,
      source,
    }: {
      appId: string
      entities: string[]
      source?: IntegrationSource
    }) => {
      assertRolloutResolved(rollout.isResolved)
      return (await gateway.resyncCustomFields(source, appId, entities)).data
    },
    onSuccess: (_, { appId, source }) => {
      client.invalidateQueries({ queryKey: externalAppsQueryKeys().customFields(appId, source) })
      successToast({ title: 'Success', description: 'Custom fields synced successfully' })
    },
    onError: (err) => {
      const errorText = getErrorText(err as AxiosError<ApiResponse<{ error: string }>>)
      errorToast({
        title: 'Error',
        description: errorText?.toString() || 'Failed to sync custom fields',
      })
    },
  })
}

export const useGrantAccessMutation = (gatewayOverride?: IntegrationSourceGateway) => {
  const client = useQueryClient()
  const rollout = useIntegrationRollout()
  const gateway = gatewayOverride ?? rollout.gateway
  return useMutation({
    mutationFn: async ({
      appId,
      payload,
      source,
    }: {
      appId: string
      payload?: Record<string, unknown>
      source?: IntegrationSource
    }) => {
      assertRolloutResolved(rollout.isResolved)
      return (await gateway.grantAccess(source, appId, payload)).data
    },
    onSuccess: (_data, { appId, source }) => {
      client.invalidateQueries({ queryKey: externalAppsQueryKeys().externalOrgAppsBase })
      client.invalidateQueries({ queryKey: externalAppsQueryKeys().accessRecords(appId, source) })
      successToast({ title: 'Success', description: 'Access granted successfully' })
    },
    onError: (err) => {
      const errorText = getErrorText(err as AxiosError<ApiResponse<{ error: string }>>)
      errorToast({ title: 'Error', description: errorText?.toString() || 'Failed to grant access' })
    },
  })
}

export const useRevokeAccessMutation = (gatewayOverride?: IntegrationSourceGateway) => {
  const client = useQueryClient()
  const rollout = useIntegrationRollout()
  const gateway = gatewayOverride ?? rollout.gateway
  return useMutation({
    mutationFn: async ({
      appId,
      payload,
      source,
    }: {
      appId: string
      payload?: Record<string, unknown>
      source?: IntegrationSource
    }) => {
      assertRolloutResolved(rollout.isResolved)
      return (await gateway.revokeAccess(source, appId, payload)).data
    },
    onSuccess: (_data, { appId, source }) => {
      client.invalidateQueries({ queryKey: externalAppsQueryKeys().externalOrgAppsBase })
      client.invalidateQueries({ queryKey: externalAppsQueryKeys().accessRecords(appId, source) })
      successToast({ title: 'Success', description: 'Access revoked successfully' })
    },
    onError: (err) => {
      const errorText = getErrorText(err as AxiosError<ApiResponse<{ error: string }>>)
      errorToast({
        title: 'Error',
        description: errorText?.toString() || 'Failed to revoke access',
      })
    },
  })
}
